import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const ToggleContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: var(--color-blanco);
  border-radius: 200px;
  padding: 5px;
  width: fit-content;
  max-width: 100%;

  /* Sticky behaviour — applies to ALL screen sizes */
  position: ${({ $sticky }) => ($sticky ? 'fixed' : 'relative')};
  top: ${({ $sticky }) => ($sticky ? 'calc(var(--header-height, 70px) + 12px)' : 'auto')};
  left: ${({ $sticky }) => ($sticky ? '50%' : 'auto')};
  transform: ${({ $sticky, $visible }) =>
    $sticky
      ? `translateX(-50%) translateY(${$visible ? '0' : '-200px'})`
      : 'none'};
  margin: ${({ $sticky }) => ($sticky ? '0' : '0 auto -3vh auto')};
  z-index: ${({ $sticky }) => ($sticky ? 400 : 10)};
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${({ $sticky }) =>
    $sticky ? '0 6px 24px rgba(0, 0, 0, 0.14)' : '0 4px 16px rgba(0, 0, 0, 0.08)'};

  @media (min-width: 769px) {
    top: ${({ $sticky }) => ($sticky ? '150px' : 'auto')};
  }

  @media (max-width: 768px) {
    width: ${({ $sticky }) => ($sticky ? 'calc(100% - 40px)' : 'calc(100% - 20px)')};
    margin: ${({ $sticky }) => ($sticky ? '0' : '0 auto 20px auto')};
  }
`;

/* Indicador (pill) que se desliza por detrás de las opciones */
const Slider = styled.span`
  position: absolute;
  top: 5px;
  bottom: 5px;
  left: 5px;
  width: calc(50% - 5px);
  border-radius: 40px;
  background-color: ${({ $activeColor }) => $activeColor};
  transform: translateX(${({ $index }) => ($index === 1 ? '100%' : '0%')});
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.35s ease;
  z-index: 1;
  pointer-events: none;
`;

const ToggleOption = styled.button`
  position: relative;
  z-index: 2;
  box-sizing: border-box;
  width: 100%;
  text-align: center;
  background-color: transparent;
  color: ${({ $active }) => ($active ? 'var(--color-blanco)' : 'var(--color-marron-principal)')};
  border: none;
  border-radius: 40px;
  padding: 12px 56px;
  font-size: clamp(0.75rem, 2.2vw, 1.1rem);
  font-weight: 500;
  cursor: pointer;
  transition: color 0.3s ease, transform 0.2s ease;
  font-family: var(--font-family-secondary);
  white-space: nowrap;
  line-height: 1.3;

  &:hover {
    color: ${({ $active }) => ($active ? 'var(--color-blanco)' : 'var(--color-bordo-secundario)')};
  }

  &:active {
    transform: scale(0.96);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    white-space: normal;         /* Permite wrap en pantallas medianas si el texto es largo */
    word-break: break-word;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: clamp(0.7rem, 3.5vw, 0.88rem);
    white-space: normal;
    word-break: break-word;
  }

  @media (max-width: 360px) {
    padding: 7px 8px;
    font-size: clamp(0.65rem, 3.8vw, 0.8rem);
  }
`;

export default function ToggleSelection({ seccionActiva, onSeccionChange, textoPerfumeria, textoHogar }) {
  const [visible, setVisible] = useState(false);
  const [sticky, setSticky] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;
      const threshold = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 70;

      if (currentY > threshold) {
        setSticky(true);
        if (diff < -5) setVisible(true);
        else if (diff > 5) setVisible(false);
      } else {
        setSticky(false);
        setVisible(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const index = seccionActiva === 'hogar' ? 1 : 0;
  const activeColor = seccionActiva === 'hogar' ? 'var(--color-hogar)' : 'var(--color-bordo-tercero)';

  return (
    <ToggleContainer $visible={visible} $sticky={sticky}>
      <Slider $index={index} $activeColor={activeColor} aria-hidden="true" />
      <ToggleOption
        $active={seccionActiva === 'perfumeria'}
        onClick={() => onSeccionChange('perfumeria')}
      >
        {textoPerfumeria}
      </ToggleOption>
      <ToggleOption
        $active={seccionActiva === 'hogar'}
        onClick={() => onSeccionChange('hogar')}
      >
        {textoHogar}
      </ToggleOption>
    </ToggleContainer>
  );
}
